/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type { ErrorMessage } from '@/types';
import type {
  AppGlobalState,
  GooglePayGlobalState,
  LocationsGlobalState,
  PayPalGlobalState,
  PreferredStoreGlobalState,
  RewardsGlobalState,
  SaveForLaterGlobalState,
  SearchGlobalState,
  LoginConfirmationState,
} from '@/types/globalState';
import { isDev } from '@/utils/isDev';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, type SetStateAction } from 'react';

export type GlobalStateKeys =
  | 'appData'
  | 'rewardsData'
  | 'paypal'
  | 'store'
  | 'googlePay'
  | 'saveForLater'
  | 'search'
  | 'loginConfirmation'
  | 'locations';

type GlobalStateValues = {
  appData: AppGlobalState;
  rewardsData: RewardsGlobalState;
  paypal: PayPalGlobalState;
  store: PreferredStoreGlobalState;
  googlePay: GooglePayGlobalState;
  saveForLater: SaveForLaterGlobalState;
  search: SearchGlobalState;
  loginConfirmation: LoginConfirmationState;
  locations: LocationsGlobalState;
};

type Value<Key> = Key extends GlobalStateKeys
  ? GlobalStateValues[Key]
  : ErrorMessage<'Invalid global state key'>;

export const globalStateKeys = new Set<GlobalStateKeys>([
  'appData',
  'rewardsData',
  'paypal',
  'store',
  'googlePay',
  'saveForLater',
  'search',
  'loginConfirmation',
  'locations',
]);

const getFullKey = (key: GlobalStateKeys) => ['globalState', key] as const;

/**
 * Returns an array with the current state and a setter function to update it.
 *
 * Read more in https://dxp-b2c-docs.autozone.com/applications/diy-web-app/state-management/global-state/
 */
export const useGlobalState = <Key extends GlobalStateKeys>(
  key: Key,
): [state: Value<Key>, setState: (updater: SetStateAction<Partial<Value<Key>>>) => void] => {
  const queryClient = useQueryClient();

  const { data: state } = useQuery({
    queryKey: getFullKey(key),
    queryFn: () => {
      throw new Error("This query shouldn't be refetched");
    },
    enabled: false,
    gcTime: Infinity,
    staleTime: Infinity,
  });

  const setState = useCallback(
    (updater: SetStateAction<Partial<Value<Key>>>) => {
      setGlobalStateQueryData(key, updater, queryClient);
    },
    [queryClient, key],
  );

  if (!state && isDev()) {
    throw new Error("Set up the initial value in 'setupGlobalState.server.ts'");
  }

  return [state!, setState];
};

/**
 * Get the current global state value.
 *
 * Read more in https://dxp-b2c-docs.autozone.com/applications/diy-web-app/state-management/global-state/
 */
export const getGlobalStateQueryData = <Key extends GlobalStateKeys>(
  key: Key,
  queryClient: QueryClient,
): Value<Key> | undefined => {
  if (!globalStateKeys.has(key)) {
    throw new Error('Invalid global state key');
  }
  return queryClient.getQueryData<Value<Key>>(getFullKey(key));
};

/**
 * Update a global state value.
 *
 * Read more in https://dxp-b2c-docs.autozone.com/applications/diy-web-app/state-management/global-state/
 */
export const setGlobalStateQueryData = <Key extends GlobalStateKeys>(
  key: Key,
  updater: SetStateAction<Partial<Value<Key>>>,
  queryClient: QueryClient,
): void => {
  if (!globalStateKeys.has(key)) {
    throw new Error('Invalid global state key');
  }
  queryClient.setQueryData<Value<Key>>(getFullKey(key), (prevState) => {
    if ('call' in updater) {
      if (prevState) {
        return { ...prevState, ...updater(prevState) };
      } else {
        throw new Error("Can't use setter callback without prevState. Set the value directly");
      }
    } else {
      // @ts-expect-error prevState should contail all keys
      const newState: Value<Key> = {
        ...prevState,
        ...updater,
      };

      return newState;
    }
  });
};
